import React from 'react';
import { Box, InputAdornment, TextField } from '@mui/material';
import { PRES, SPACE, TYPE } from '../chrome';
import { COPY } from '../presentationCopy';
import { PresentationSettingsState } from '../presentationSettings';
import { AdminLabeledControl } from './AdminInfoLabel';
import AdminSettingCard from './AdminSettingCard';
import AdminVisibilitySwitch from './AdminVisibilitySwitch';
import { percentToRatio, ratioToPercent } from './adminSettingsState';
import { adminFieldSx } from './adminFormSx';

interface AdminHealthSectionProps {
  draft: PresentationSettingsState;
  setDraft: React.Dispatch<React.SetStateAction<PresentationSettingsState>>;
}

const HEALTH_RULES: Array<{ id: string; label: string; help: string }> = [
  { id: 'text-overflow', label: COPY.settingsHealthTextOverflow, help: COPY.settingsHealthTextOverflowHelp },
  { id: 'table-overflow', label: COPY.settingsHealthTableOverflow, help: COPY.settingsHealthTableOverflowHelp },
  { id: 'chart-empty', label: COPY.settingsHealthChartEmpty, help: COPY.settingsHealthChartEmptyHelp },
  { id: 'outside-safe-area', label: COPY.settingsHealthSafeArea, help: COPY.settingsHealthSafeAreaHelp },
  { id: 'locked-font', label: COPY.settingsHealthLockedFont, help: COPY.settingsHealthLockedFontHelp },
  { id: 'font-unapproved', label: COPY.settingsHealthFontUnapproved, help: COPY.settingsHealthFontUnapprovedHelp },
  { id: 'locked-color', label: COPY.settingsHealthLockedColor, help: COPY.settingsHealthLockedColorHelp },
  { id: 'missing-media', label: COPY.settingsHealthMissingMedia, help: COPY.settingsHealthMissingMediaHelp },
  { id: 'asset-missing', label: COPY.settingsHealthAssetMissing, help: COPY.settingsHealthAssetMissingHelp },
  { id: 'asset-access', label: COPY.settingsHealthAssetAccess, help: COPY.settingsHealthAssetAccessHelp },
];

const AdminHealthSection: React.FC<AdminHealthSectionProps> = ({ draft, setDraft }) => {
  const setRatio = (key: 'overflowRatio' | 'underfillRatio' | 'rewriteFitMinRatio', percent: number) => {
    setDraft((current) => ({
      ...current,
      health: { ...current.health, [key]: percentToRatio(percent) },
    }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${SPACE[5]}px`, maxWidth: 640 }}>
      <AdminSettingCard title={COPY.settingsHealthRulesCard} description={COPY.settingsHealthRulesHelp}>
        {HEALTH_RULES.map((rule) => (
          <Box key={rule.id} sx={{ mb: 1.5 }}>
            <AdminVisibilitySwitch
              checked={!draft.health.disabledCodes.includes(rule.id)}
              label={rule.label}
              onChange={(checked) => {
                const disabledCodes = checked
                  ? draft.health.disabledCodes.filter((code) => code !== rule.id)
                  : [...draft.health.disabledCodes, rule.id];
                setDraft((current) => ({ ...current, health: { ...current.health, disabledCodes } }));
              }}
            />
            <Box sx={{ ml: 1.5, mt: -0.5, color: PRES.textTertiary, fontFamily: TYPE.ui, ...TYPE.caption }}>
              {rule.help}
            </Box>
          </Box>
        ))}
      </AdminSettingCard>
      <AdminSettingCard title={COPY.settingsHealthThresholdsCard}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <AdminLabeledControl label={COPY.settingsHealthOverflow} hint={COPY.settingsHealthOverflowHelp}>
            {({ id, describedBy }) => (
              <TextField
                id={id}
                size="small"
                type="number"
                fullWidth
                value={ratioToPercent(draft.health.overflowRatio)}
                onChange={(event) => setRatio('overflowRatio', Number(event.target.value))}
                inputProps={{ 'aria-describedby': describedBy }}
                InputProps={{ endAdornment: <InputAdornment position="end">{COPY.settingsPercent}</InputAdornment> }}
                sx={adminFieldSx()}
              />
            )}
          </AdminLabeledControl>
          <AdminLabeledControl label={COPY.settingsHealthUnderfill} hint={COPY.settingsHealthUnderfillHelp}>
            {({ id, describedBy }) => (
              <TextField
                id={id}
                size="small"
                type="number"
                fullWidth
                value={ratioToPercent(draft.health.underfillRatio)}
                onChange={(event) => setRatio('underfillRatio', Number(event.target.value))}
                inputProps={{ 'aria-describedby': describedBy }}
                InputProps={{ endAdornment: <InputAdornment position="end">{COPY.settingsPercent}</InputAdornment> }}
                sx={adminFieldSx()}
              />
            )}
          </AdminLabeledControl>
          <AdminLabeledControl label={COPY.settingsRewriteFit} hint={COPY.settingsRewriteFitHelp}>
            {({ id, describedBy }) => (
              <TextField
                id={id}
                size="small"
                type="number"
                fullWidth
                value={ratioToPercent(draft.health.rewriteFitMinRatio)}
                onChange={(event) => setRatio('rewriteFitMinRatio', Number(event.target.value))}
                inputProps={{ 'aria-describedby': describedBy }}
                InputProps={{ endAdornment: <InputAdornment position="end">{COPY.settingsPercent}</InputAdornment> }}
                sx={adminFieldSx()}
              />
            )}
          </AdminLabeledControl>
        </Box>
      </AdminSettingCard>
    </Box>
  );
};

export default AdminHealthSection;
